import React, { useContext, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import {
  Box,
  Flex,
  Hide,
  SrOnly
} from 'pcln-design-system'
import pclnExperimentation from '@pcln/experimentation'
import { AceContentProvider } from '@pcln/ace-content-react'
import NewSearchForms from '../../components/NEW_SEARCH_SearchForms'
import WhyPriceline from '../../components/why-priceline/WhyPriceline'
import Content from '../../constants/Content'
import HotelCollectionFeature from '../../components/HotelCollectionFeature'
import HotelSOPQFeature from '../../components/HotelSOPQFeature'
import HotelVIPFeature from '../../components/HotelVIPFeature'
import { capitalizeLetter } from '../../utils/string-helpers'
import queryString from 'query-string'
import {
  ANALYTICS_CATEGORY,
  HOME_PAGE_TAB_ORDER_ARRAY,
  HOTEL_PRODUCT_TYPE,
  VACATION_PACKAGE_PRODUCT_TYPE
} from '../../constants/Constants'
import { AppDownloadButton, LoaderCSS } from '../../shared'
import { PopularDestinations } from '../../components/popular-destinations'
import { ScrollableDealModule } from '../../components/scrollable-deal-module'
import EmailSignup from '../../components/email-signup'
// TODO: Refactor CreditCard component and content directory structure
import CreditCard from '../../components/CreditCard'
import { CreditCardContent } from '../../components/CreditCard/Content'
import {
  AdBanner,
  PromotionBanner
} from '../../components/promos'
import {
  downloadAndroidApp,
  downloadiOSApp,
  firePreselectedTab,
  openHotelExpressDealModal
} from '../../actions/GoogleAnalyticsClickActions'
import { calcTotalBannersPaddingTop } from '../../utils/global-banner-padding'
import getPackageDeals from '../../utils/get-package-deals'
import { AnalyticsProvider } from '@pcln/react-analytics'
import packageJson from '../../../../package'
import { renderCobrandBanner } from '../../utils/cobrand-util'
import { BootstrapDataContext, ExperimentContext, FormVisualStateContainer } from '../../contexts'
import '../../shared/GlobalStyle'
import PostLoadWebFontsThemeProvider from '../../components/PostLoadWebFontsThemeProvider'
import ButtonLink from '../../components/ButtonLink'
import { evolve, is, isEmpty, map, mergeAll, prop, replace, when } from 'ramda'
import Hero from '../../components/Hero'
import RecentSearchCards from '../../components/RecentSearchCards'

const { home: HOME_CONTENT } = Content
const { promos = {} } = HOME_CONTENT
const {
  appDownload = {},
  disneyVacations = {}
} = promos

const {
  linkUrls
} = appDownload

const processAppDownloadButtons = () => {
  const links = [
    { link: linkUrls.android, storeType: 'android', clickAction: downloadAndroidApp },
    { link: linkUrls.iOS, storeType: 'iOS', clickAction: downloadiOSApp }
  ].map(({ storeType, link, clickAction }, index) => <AppDownloadButton key={index} linkUrl={link} storeType={storeType} clickAction={clickAction} />)

  return <Box>{links}</Box>
}

const disneyButton = [
  <ButtonLink px={5} key={1} href={disneyVacations.linkUrl} target='_blank' data-autobot-element-id='DISNEY_BUTTON_MORE_INFO'>
    More Info
    <SrOnly>Walt Disney Attractions</SrOnly>
  </ButtonLink>
]

const { creditCard: CREDIT_CARD_CONTENT } = CreditCardContent

const DestinationSection = styled.section`
  max-width: 1280px !important;
`

function pricingDestination (packageDeals) {
  if (packageDeals.length > 1) {
    return (
      <DestinationSection>
        <Box mt={4}>
          <ScrollableDealModule
            content={{
              heading: "Don't miss these hotel + flight deals",
              headingMobile: "Don't miss these deals",
              dataArray: packageDeals
            }}
            handleNavOnClick={href => window.open(href)}
          />
        </Box>
      </DestinationSection>
    )
  }
}

function Home ({ location }) {
  const [query, setQuery] = useState(queryString.parse(location.search))
  useEffect(() => {
    setQuery(queryString.parse(location.search))
  }, [location.search])

  const [selectedTab, setSelectedTab] = useState('hotels')
  useEffect(() => {
    function __determineProductTab () {
      const defaultProduct =
        query.defproduct || query.tab || HOTEL_PRODUCT_TYPE

      return HOME_PAGE_TAB_ORDER_ARRAY.indexOf(defaultProduct)
    }

    setSelectedTab(__determineProductTab())
  }, [query])

  const [deferLoad, setDeferLoad] = useState(true)
  useEffect(() => {
    setDeferLoad(false)
  }, [])

  useEffect(() => {
    window.addEventListener('load', renderCobrandBanner)
    return () => window.removeEventListener('load', renderCobrandBanner)
  }, [])

  useEffect(() => {
    function reload () {
      window.location.reload()
    }

    const GLOBAL_EVENTS = [{
      name: 'GLOBAL_PCLN_CUSTOMER_SIGNOUT',
      callback: reload
    },
    {
      name: 'GLOBAL_PCLN_CUSTOMER_SIGNIN',
      callback: reload
    }]

    const pclnEventDispatcher = window.PCLN && window.PCLN.pclnEventDispatcher
    function registerPclnEvents () {
      if (pclnEventDispatcher) {
        GLOBAL_EVENTS.forEach(event =>
          pclnEventDispatcher.register(event.name, event.callback)
        )
      }
    }

    function removePclnEvents () {
      if (pclnEventDispatcher) {
        GLOBAL_EVENTS.forEach(event =>
          pclnEventDispatcher.unregister(event.name)
        )
      }
    }

    // This following line is the first step in changing of global event(s)
    window.addEventListener('load', registerPclnEvents)

    return () => removePclnEvents()
  }, [])

  const [packageDeals, setPackageDeals] = useState([])
  const { PKG_HOME_DESTINATION_DEALS_OCC_DATES_UPDATED = 'DEFAULT' } = useContext(ExperimentContext)

  const {
    shouldShowEDModal = false,
    isMobile = false,
    locale = {},
    aceContent = {},
    pclnData = {},
    signInStatus = {}
  } = useContext(BootstrapDataContext)

  const isSignedIn = signInStatus.signedIn || signInStatus.passiveSignIn

  const processAceContent = ({ contentValues = {} }, firstName) => {
    const {
      HOMEPAGE,
      SHARED,
      VIPHOMEPAGE
    } = contentValues

    const mergedAceContent = mergeAll([HOMEPAGE, SHARED, VIPHOMEPAGE])
    return map(
      evolve({ value: when(is(String), replace('{firstName}', firstName)) }),
      mergedAceContent)
  }
  const [parsedAceContent, setParsedAceContent] = useState({})

  useEffect(() => {
    const processedAceContent = processAceContent(aceContent, pclnData.firstName)
    setParsedAceContent(processedAceContent)
  }, [aceContent, pclnData.firstName])

  useEffect(() => {
    getPackageDeals(packageDeals => {
      setPackageDeals(packageDeals)
    },
    {
      isVariant: PKG_HOME_DESTINATION_DEALS_OCC_DATES_UPDATED === 'VARIANT',
      latitude: prop('latitude', locale),
      longitude: prop('longitude', locale)
    })
  }, [PKG_HOME_DESTINATION_DEALS_OCC_DATES_UPDATED, setPackageDeals, locale])

  useEffect(() => {
    function trackSelectedTab (selectedTab) {
      const productName = HOME_PAGE_TAB_ORDER_ARRAY[selectedTab]
      const selectedTabName = productName === VACATION_PACKAGE_PRODUCT_TYPE ? 'packages' : productName
      const productGA = capitalizeLetter(selectedTabName)
      firePreselectedTab(productGA)
    }

    trackSelectedTab(selectedTab)
  }, [selectedTab])

  useEffect(() => {
    pclnExperimentation.fireImpression(
      'PKG_HOME_DESTINATION_DEALS_OCC_DATES_UPDATED',
      {},
      { ga: { dimension: 'dimension91', teamName: 'PACKAGES' } }
    )

    pclnExperimentation.fireImpression(
      'HP_LP_PACKAGES_CACHE_POPULAR_DESTINATIONS',
      {},
      { ga: { dimension: 'dimension92', teamName: 'PACKAGES' } }
    )

    // TODO: PKG_HOTEL_TAB_PACKAGE_OPTION cleanup
    pclnExperimentation.fireImpression(
      'PKG_HOTEL_TAB_PACKAGE_OPTION',
      {},
      { ga: { dimension: 'dimension195', teamName: 'PACKAGES' } }
    )
  }, [])

  useEffect(() => {
    calcTotalBannersPaddingTop()
  }, [])

  // todo this can probably get removed. there is a flash when the fonts load
  useEffect(() => {
    // TODO: Workaround to address FOUC. Need to research a more optimal solution
    const rootWrapper = document.getElementById('pcln-app')
    rootWrapper.removeAttribute('style')
  }, [])

  useEffect(() => {
    if (shouldShowEDModal) {
      if (document.readyState === 'complete') {
        openHotelExpressDealModal()
      } else {
        window.addEventListener('load', openHotelExpressDealModal)
      }
    }
  }, [shouldShowEDModal])

  const heroProps = {
    img: HOME_CONTENT.heroBackgroundHatchback,
    description: 'Road Trip',
    backgroundPosition: 'center'
  }

  return (
    <PostLoadWebFontsThemeProvider fontSettings={['Montserrat:500,700']}>
      <AceContentProvider content={isEmpty(parsedAceContent) ? processAceContent(aceContent, pclnData.firstName) : parsedAceContent}>
        <AnalyticsProvider
          appName={packageJson.name}
          appVersion={packageJson.version}
          productId='16'
          gaCategory={ANALYTICS_CATEGORY}
        >
          <Box id='home' className='pcln-multi-occ'>
            <Box id='main'>
              <Box id='home-body'>
                <section
                  id='hero'
                  className='wide'
                >
                  <Hero {...heroProps}>
                    <Flex width={1}>
                      <FormVisualStateContainer.Provider>
                        <NewSearchForms />
                      </FormVisualStateContainer.Provider>
                    </Flex>
                  </Hero>
                  <Hide xs sm>
                    <RecentSearchCards />
                    <Hide md py={3}>
                      <PromotionBanner />
                    </Hide>
                    <WhyPriceline />
                    {deferLoad
                      ? <LoaderCSS />
                      : (
                        <Box>
                          {(pricingDestination(packageDeals))}
                          <AdBanner
                            smallHeadingText={appDownload.smallHeadingText}
                            headingText={appDownload.headingText}
                            contentTextPt1={appDownload.contentTextPt1}
                            contentTextPt2={appDownload.contentTextPt2}
                            contentTextPt3={appDownload.contentTextPt3}
                            buttons={processAppDownloadButtons().props.children}
                            imageSize={'307px 383px'}
                            image={appDownload.image}
                            imageWebP={appDownload.imageWebP}
                          />
                          <EmailSignup HOME_CONTENT={HOME_CONTENT.couponBanner} />
                          {!isMobile && (
                            <DestinationSection>
                              <PopularDestinations
                                routeLocationObject={location}
                              />
                            </DestinationSection>
                          )}
                          <section className={`featured-entry`}>
                            <HotelSOPQFeature
                              entryContent={HOME_CONTENT.featuredEntry}
                              formContent={HOME_CONTENT.forms.hotels}
                              showMobileClass='mobile-seti'
                            />
                            {
                              isSignedIn
                                ? <HotelVIPFeature />
                                : <HotelCollectionFeature />
                            }
                          </section>
                          <AdBanner
                            smallHeadingText={disneyVacations.smallHeadingText}
                            headingText={disneyVacations.headingText}
                            contentTextPt1={disneyVacations.contentTextPt1}
                            buttons={disneyButton}
                            image={disneyVacations.image}
                            imageWebP={disneyVacations.imageWebP}
                            imageSize={'524px 322px'}
                            clickActions={{
                              android: downloadAndroidApp,
                              iOS: downloadiOSApp
                            }}
                          />
                          <CreditCard
                            titleText={CREDIT_CARD_CONTENT.titleText}
                            bodyText={CREDIT_CARD_CONTENT.bodyText}
                            buttonText={CREDIT_CARD_CONTENT.buttonText}
                            image={CREDIT_CARD_CONTENT.image}
                            imageWebP={CREDIT_CARD_CONTENT.imageWebP}
                            legalText={CREDIT_CARD_CONTENT.legalText}
                            buttonLink={CREDIT_CARD_CONTENT.buttonLink}
                          />
                        </Box>
                      )}
                  </Hide>
                </section>
              </Box>
            </Box>
          </Box>
        </AnalyticsProvider>
      </AceContentProvider>
    </PostLoadWebFontsThemeProvider>
  )
}

Home.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string
  })
}

export default Home
